import Skeleton from "@dashboard/components/Skeleton";
import { StockAvailability } from "@dashboard/graphql";
import { productListUrl } from "@dashboard/products/urls";
import { Box, List, Text, Tooltip } from "@saleor/macaw-ui-next";
import React from "react";
import { defineMessages, useIntl } from "react-intl";
import { Link } from "react-router-dom";

const MAX_ROWS = 8;

const messages = defineMessages({
  unbuyable: {
    defaultMessage: "Out of stock",
    id: "PgNWXL",
    description: "out of stock card, product row caption",
  },
  nothingOutOfStock: {
    defaultMessage: "Nothing is out of stock",
    id: "EBsEFg",
    description: "out of stock card, empty state",
  },
  moreProducts: {
    defaultMessage: "… {count} more",
    id: "55iI2s",
    description: "out of stock card, products beyond those listed",
  },
  viewAll: {
    defaultMessage: "View all out of stock",
    id: "csm1M2",
    description: "out of stock card, link to the filtered product list",
  },
});

export interface OutOfStockProduct {
  id: string;
  name: string;
  thumbnail?: { url: string } | null;
}

interface HomeOutOfStockPanelProps {
  products: OutOfStockProduct[];
  totalCount: number;
  loading: boolean;
}

export const HomeOutOfStockPanel = ({
  products,
  totalCount,
  loading,
}: HomeOutOfStockPanelProps) => {
  const intl = useIntl();

  if (loading) {
    return (
      <Box padding={3} display="flex" flexDirection="column" gap={2}>
        <Skeleton />
        <Skeleton />
        <Skeleton />
      </Box>
    );
  }

  if (products.length === 0) {
    return (
      <Box padding={3}>
        <Text size="small" color="default2">
          {intl.formatMessage(messages.nothingOutOfStock)}
        </Text>
      </Box>
    );
  }

  const shown = products.slice(0, MAX_ROWS);
  const remaining = totalCount - shown.length;

  return (
    <Box display="flex" flexDirection="column">
      <List>
        {shown.map(product => (
          <List.Item
            key={product.id}
            borderColor="default1"
            borderWidth={1}
            borderTopStyle="solid"
            padding={3}
          >
            <Tooltip>
              <Tooltip.Trigger>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  width="100%"
                  gap={3}
                >
                  {/* minWidth 0 lets this shrink past its content so the
                      name can ellipsize instead of pushing the caption. */}
                  <Box display="flex" alignItems="center" gap={3} minWidth={0}>
                    <Box
                      as="img"
                      src={product.thumbnail?.url}
                      alt=""
                      width={6}
                      height={6}
                      flexShrink="0"
                      borderRadius={2}
                      borderColor="default1"
                      borderWidth={1}
                      borderStyle="solid"
                      objectFit="scale-down"
                    />
                    <Text size="small" ellipsis>
                      {product.name}
                    </Text>
                  </Box>
                  {/* Product-level: the filter means no variant is buyable. */}
                  <Text
                    size="small"
                    color="default2"
                    flexShrink="0"
                    whiteSpace="nowrap"
                  >
                    {intl.formatMessage(messages.unbuyable)}
                  </Text>
                </Box>
              </Tooltip.Trigger>
              <Tooltip.Content>
                <Box __maxWidth={320}>
                  <Text size="small">{product.name}</Text>
                </Box>
              </Tooltip.Content>
            </Tooltip>
          </List.Item>
        ))}
      </List>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        padding={3}
        borderColor="default1"
        borderWidth={1}
        borderTopStyle="solid"
      >
        <Text size="small" color="default2">
          {remaining > 0 &&
            intl.formatMessage(messages.moreProducts, { count: remaining })}
        </Text>
        <Link
          to={productListUrl({
            stockStatus: StockAvailability.OUT_OF_STOCK,
          })}
        >
          <Text size="small" color="accent1">
            {intl.formatMessage(messages.viewAll)}
          </Text>
        </Link>
      </Box>
    </Box>
  );
};
